import { Option, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { CommonLabelAndValueList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormattedInput } from '../../shared-components/form/FormattedInput';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import { PhoneNumberField, PhoneNumberInput } from '../../shared-components/phone-number-input';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';

interface CompanyInformationDetailFormProps {
  isViewOnly: boolean;
}

const CompanyInformationDetailFormImpl: React.FC<CompanyInformationDetailFormProps> = (props) => {
  const {
    isViewOnly,
  } = props;

  const {
    nominationForStaffPageUiStore,
    countryStore,
    partnerTypeStore,
  } = useStores();

  const validation = nominationForStaffPageUiStore.getCompanyInformationValidation(
    partnerTypeStore.otherPartnerTypeId,
  );

  return (
    <FlexBox
      isFullWidth
      as='section'
      className='nomination-details__fields'
    >
      <Text
        as='h3'
        block
        weight='semibold'
      >
        Company Information
      </Text>
      <FlexBox
        isFullWidth
        direction='column'
        className='fields__list'
      >
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId='partner-id'
            label='Partner ID:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.mpnId}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.mpnId?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.mpnId} />
                    : undefined
                }
                onChange={mpnId => nominationForStaffPageUiStore.setOrganizationBodyRequest('mpnId', mpnId)}
                inputProps={{
                  'aria-labelledby': 'partner-id',
                  type: 'number',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.mpnId ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId='company'
            label='Company:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.companyName}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.companyName?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.companyName} />
                    : undefined
                }
                onChange={companyName => nominationForStaffPageUiStore.setOrganizationBodyRequest('companyName', companyName)}
                inputProps={{
                  'aria-labelledby': 'company',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.companyName ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId='address-1'
            label='Address 1:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.address1}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.address1?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.address1} />
                    : undefined
                }
                onChange={address1 => nominationForStaffPageUiStore.setOrganizationBodyRequest('address1', address1)}
                inputProps={{
                  'aria-labelledby': 'address-1',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.address1 ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId='address-2'
            label='Address 2:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.address2}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.address2?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.address2} />
                    : undefined
                }
                onChange={address2 => nominationForStaffPageUiStore.setOrganizationBodyRequest('address2', address2)}
                inputProps={{
                  'aria-labelledby': 'address-2',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.address2 ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId='city'
            label='City:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.city}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.city?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.city} />
                    : undefined
                }
                onChange={city => nominationForStaffPageUiStore.setOrganizationBodyRequest('city', city)}
                inputProps={{
                  'aria-labelledby': 'city',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.city ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId='state'
            label='State:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.stateProvince}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.stateProvince?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.stateProvince} />
                    : undefined
                }
                onChange={stateProvince => nominationForStaffPageUiStore.setOrganizationBodyRequest('stateProvince', stateProvince)}
                inputProps={{
                  'aria-labelledby': 'state',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.stateProvince ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId='postal-code'
            label='Postal code:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.postalCode}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.postalCode?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.postalCode} />
                    : undefined
                }
                onChange={postalCode => nominationForStaffPageUiStore.setOrganizationBodyRequest('postalCode', postalCode)}
                inputProps={{
                  'aria-labelledby': 'postal-code',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.postalCode ?? '',
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId='country'
            label='Country:'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.country?.displayName}
          >
            {
              !isViewOnly &&
              <SelectOptions
                onOptionSelect={(data) => {
                  if (!data.optionValue) return;
                  const country = countryStore.awardCountries.find(country => country.countryCode === data.optionValue);
                  if (!country) return;
                  nominationForStaffPageUiStore.setOrganizationBodyRequest('country', { ...country });
                }}
                validationMessage={
                  validation.fieldErrors.country?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.country} />
                    : undefined
                }
                dropdownProps={{
                  'aria-labelledby': 'country',
                  value: nominationForStaffPageUiStore.nominationForStaffOrganization?.country?.displayName ?? '',
                  selectedOptions: nominationForStaffPageUiStore.nominationForStaffOrganization?.country?.countryCode ? [nominationForStaffPageUiStore.nominationForStaffOrganization.country.countryCode] : [],
                }}
              >
                {
                  countryStore.awardCountries.map((country) => (
                    <Option key={country.countryCode} value={country.countryCode}>{country.displayName}</Option>
                  ))
                }
              </SelectOptions>
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            label='Company phone number:'
            labelId='company-phone-number'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.phoneNumber}
          >
            {
              !isViewOnly &&
              <PhoneNumberInput
                id={PhoneNumberField.Organization}
                ariaLabelledBy='company-phone-number'
                value={nominationForStaffPageUiStore.nominationForStaffOrganization?.phoneNumber ?? ''}
                onChange={(phoneNumber) => {
                  nominationForStaffPageUiStore.setOrganizationBodyRequest('phoneNumber', phoneNumber);
                }}
                onCountryChange={(id, newCountryCode) => {
                  nominationForStaffPageUiStore.setSelectedCountryCode(id, newCountryCode);
                }}
                error={
                  validation.fieldErrors.phoneNumber?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.phoneNumber} />
                    : undefined
                }
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            label='Website Url:'
            labelId='company-website-url'
            childrenFallback={nominationForStaffPageUiStore.nominationForStaff.nomination.organization?.websiteUrl}
          >
            {
              !isViewOnly &&
              <FormattedInput
                orientation='horizontal'
                prefix={'https://'}
                maxLength={100}
                isValidated
                value={nominationForStaffPageUiStore.nominationForStaffOrganization?.websiteUrl ?? ''}
                onChange={websiteUrl => nominationForStaffPageUiStore.setOrganizationBodyRequest('websiteUrl', websiteUrl)}
                ariaLabelledBy='company-website-url'
                errorText={
                  validation.fieldErrors.websiteUrl?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.websiteUrl} />
                    : undefined
                }
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}

export const CompanyInformationDetailForm = observer(CompanyInformationDetailFormImpl);
